/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { get } from 'lodash';
import { Button, Dropdown } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { StyleSheet, css } from 'aphrodite/no-important';
import { graphql } from '@apollo/client/react/hoc';
import mutateLogout from './graphql/mutateLogout';
import withRoles from '../hoc/withRoles';
import { NODE_API_URL, API_HTTP_URL } from '../settings.js';
import { TeamListItemAvatar } from './team/TeamListItem';

const sx = StyleSheet.create({
  dropdownIcon: {
    marginLeft: '5px',
    marginRight: '5px',
    transform: 'translateY(5px)',
    alignItems: 'inherit',
  },
  userIcon: {
    height: '27px !important',
    width: '27px !important',
    marginRight: '5px',
    padding: '3px',
    transform: 'translateY(-5px)',
  },
  settingsDropdownContainer: {
    padding: '0.5rem !important',
    display: 'flex',
    transform: 'translateY(4px)',
  },
  btnPadding: {
    padding: '0px !important',
    display: 'flex',
    alignItems: 'center',
  },
});

const getSettingsItems = (currentUser, isSystemAdmin, isPbsTeam, hasRole, handlers) => {
  const callCenterFeatureFlag = get(currentUser, 'feature_flags.callCenter');
  const items = [
    {
      key: '1',
      label: <div onClick={() => handlers.handleClickAccount()}>My Account</div>,
    },
  ];
  if (currentUser && !isSystemAdmin) {
    items.push({
      key: '2',
      label: <div onClick={() => handlers.handleClickTeam()}>My Team</div>,
    });
  }
  items.push(
    {
      key: '12',
      label: <div onClick={() => handlers.handleClickHelp()}>Help</div>,
    },
    {
      key: '13',
      label: <div onClick={() => handlers.handleLogout()}>Sign Out</div>,
    },
  );

  return items;
};

const SettingsTopBarDropdown = ({
  isSystemAdmin,
  isPbsTeam,
  hasRole,
  currentUser,
  currentUserName,
  mutateLogout,
}) => {
  const navigate = useNavigate();
  const handlers = {
    handleClickAccount: () => navigate('/settings/account'),
    handleClickHelp: () => navigate('/settings/help'),
    handleClickTeam: () => navigate('/settings/team'),
    handleLogout: async () => {
      const completeApplicationCleanup = () => {
        try {
          // Clear all browser storage
          sessionStorage.clear();
          localStorage.clear();
          
          // Clear all authentication cookies
          const cookiesToClear = [
            'auth-session-cookie',
            'session-refresh-cookie', 
            'isNewBrowserSession',
            'lastUserActivity'
          ];
          
          cookiesToClear.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.zipdrug.com;`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          });
          
        } catch (error) {
          console.error('Error during application cleanup:', error);
        }
      };

      try {
        // Call logout mutation (handles OKTA session clearing)
        const result = await mutateLogout();
        
        // Perform complete application cleanup
        completeApplicationCleanup();
        
        // Check if OKTA logout URL was returned and redirect accordingly
        if (result?.data?.logout?.oktaLogoutUrl) {
          // OKTA user - redirect to OKTA logout URL
          window.location.href = result.data.logout.oktaLogoutUrl;
        } else {
          // SAML user or no OKTA session - redirect to signin
          navigate('/signin');
        }
      } catch (error) {
        console.error('Logout error:', error);
        
        // Force complete cleanup even on error to prevent session persistence
        completeApplicationCleanup();
        
        // Redirect to signin page
        navigate('/signin');
      }
    },
  };

  const items = getSettingsItems(currentUser, isSystemAdmin, isPbsTeam, hasRole, handlers);

  return (
    <Button type="text" className={css(sx.btnPadding)}>
      <Dropdown menu={{ items }} trigger={['click']}>
        <div className={css(sx.settingsDropdownContainer)}>
          {<TeamListItemAvatar user={currentUser} className={css(sx.userIcon)} />}
          {`${currentUserName}`}
          {<DownOutlined className={css(sx.dropdownIcon)} />}
        </div>
      </Dropdown>
    </Button>
  );
};

SettingsTopBarDropdown.propTypes = {
  currentUser: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    feature_flags: PropTypes.object,
  }),
  currentUserName: PropTypes.string,
  hasRole: PropTypes.object,
  isSystemAdmin: PropTypes.bool,
  isPbsTeam: PropTypes.bool,
  mutateLogout: PropTypes.func.isRequired,
};

export default graphql(mutateLogout, { name: 'mutateLogout' })(withRoles(SettingsTopBarDropdown));
